import {Component, EventEmitter, Input, OnInit, Output} from "@angular/core";
import {emptyFoundMeasurement, FoundMeasurement} from "../found-measurement";
import {AuthenticatedUser} from "../../authentication/authenticated-user";
import {LocalizationStrings} from "../../../i18n/i18n-model";
import {getMeasurementTypeDisplayLocalizationKey} from "../../measurement-models";
import {SupportedLanguagesProvider} from "../../../tools/supported-languages-provider";
import {ListItem} from "../list-item";
import {ReleaseToggleState} from "../../../tools/release-toggles/release-toggle-state";
import {ColumnVisibility} from "../create-column-visibility";
import {AuthenticationService} from "../../authentication/authentication.service";
import {getStatusLocalizationKey} from "./get-status-localization-key";
import {getHomeStatusLocalizationKeyForInitiatedMeasurement} from "./get-home-status-localization-key";
import {shouldShowHomeStatusInStatusColumn} from "./should-show-home-status-in-status-column";
import {whichDoctorTypeIsAssignable} from "./list-item-action-buttons/which-doctor-type-is-assignable";
import {canReassignMeasurement} from "./can-reassign-measurement";
import {generateAssignedDoctorLabel} from "./generate-assigned-doctor-label";
import {I18nService} from "../../../i18n/i18n.service";

export function emptyListItem(): ListItem {
  return {
    foundMeasurement: emptyFoundMeasurement(),
    isCloseAnalysisInProgress: false,
    showReportUpload: false,
    isItemDetailsVisible: false,
    isRejectStudyInProgress: false,
    isDeleteMeasurementInProgress: false,
    isAssignTechnicianInProgress: false,
    isRestoreMeasurementInProgress: false,
    isReassignInProgress: false,
    isOpenAnalysisInProgress: false,
  };
}

export type ReportDownloadAction = "PRIMARY" | "SECONDARY";


@Component({
    selector: "app-list-item",
    templateUrl: "./list-item.component.html",
    standalone: false
})
export class ListItemComponent implements OnInit {

  feature319Released = ReleaseToggleState.getInstance().isReleased("HOME_STATUS_FOR_NURSE_AND_DO_319");

  @Input() columnVisibility: ColumnVisibility = {} as ColumnVisibility;
  @Input() item: ListItem = emptyListItem();

  @Output() measurementChanged = new EventEmitter<void>();


  user!: AuthenticatedUser;

  readonly languageFlags: Record<string, string>;

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly i18nService: I18nService,
    private readonly supportedLanguagesProvider: SupportedLanguagesProvider
  ) {
    this.languageFlags = Object.fromEntries(this.supportedLanguagesProvider.getLanguages()
      .map(definition => [
        definition.code, definition.flag
      ]));
  }

  ngOnInit() {
    this.user = this.authenticationService.getCurrentAuthenticatedUser()!;
  }

  canViewDetails() {
    return this.user.role === "DATA_OFFICER";
  }

  toggleItemDetailsVisibility() {
    this.item.isItemDetailsVisible = !this.item.isItemDetailsVisible;
  }

  getItemTypeDisplayText(): keyof LocalizationStrings {
    return getMeasurementTypeDisplayLocalizationKey({
      type: this.item.foundMeasurement.type,
      isHome: this.item.foundMeasurement.isHome,
      actualDurationDays: this.item.foundMeasurement.durationDays,
      specifiedDurationDays: this.item.foundMeasurement.specifiedHomeHolterDuration
    });
  }

  canViewAsyncJobIndicator() {
    return this.user.role === "DATA_OFFICER";
  }

  isJobPending() {
    return this.item.foundMeasurement.asyncJobStatus === "PENDING";
  }

  isJobFailed() {
    return this.item.foundMeasurement.asyncJobStatus === "FAILED";
  }

  hasJobSucceeded() {
    return this.item.foundMeasurement.asyncJobStatus === "SUCCESS";
  }

  preanalyserName() {
    if (this.item.foundMeasurement.technicianAssignType === undefined) {
      return "";
    }

    return generateAssignedDoctorLabel(
      {
        doctor: this.item.foundMeasurement.technician,
        chain: undefined,
        assignType: this.item.foundMeasurement.technicianAssignType
      },
      this.i18nService.getLocalizedString("assignDoctorSelectorGlobalTechnicianPoolOptionLabel"),
      this.i18nService.getLocalizedString("measurementsGridAssignLabelGlobalTechnicianPoolIndicator"),
    );
  }

  isDoctorSelectable(): boolean {
    return this.user.role === "DATA_OFFICER"
      && (
        ["TECHNICIAN", "DOCTOR"].includes(whichDoctorTypeIsAssignable(this.item.foundMeasurement))
        || canReassignMeasurement(this.item.foundMeasurement)
      );
  }

  shouldDisplayAssignedLabel(): boolean {
    return this.item.foundMeasurement.assignType !== undefined && !this.isDoctorSelectable();
  }

  handleMeasurementChanged() {
    this.measurementChanged.emit();
  }

  onMeasurementEdited(measurement: FoundMeasurement) {
    this.item.foundMeasurement = measurement;
    this.measurementChanged.emit();
  }

  shouldMarkCurrentDoctorInDoctorSelect() {
    return this.item.foundMeasurement.status !== "READY_FOR_ANALYSIS";
  }

  getHomeStatusLocalizationKey(): keyof LocalizationStrings {
    return getHomeStatusLocalizationKeyForInitiatedMeasurement(this.item.foundMeasurement.homeStatus!, this.item.foundMeasurement.status);
  }

  getStatusLocalizationKey(): keyof LocalizationStrings | undefined {
    return getStatusLocalizationKey(
      {
        status: this.item.foundMeasurement.status,
        role: this.user.role,
        isOrdered: this.item.foundMeasurement.hasOperatorHospital,
        isHome: this.item.foundMeasurement.isHome
      }
    );
  }

  shouldShowHomeStatusInStatusColumn() {
    return shouldShowHomeStatusInStatusColumn(
      this.user!.role,
      this.item.foundMeasurement.status,
      this.item.foundMeasurement.homeStatus
    );
  }

  isSelectingTechnicians() {
    return whichDoctorTypeIsAssignable(this.item.foundMeasurement) === "TECHNICIAN";
  }

  analyserName() {
    return generateAssignedDoctorLabel(
      this.item.foundMeasurement,
      this.i18nService.getLocalizedString("assignDoctorSelectorGlobalTechnicianPoolOptionLabel"),
      this.i18nService.getLocalizedString("measurementsGridAssignLabelGlobalTechnicianPoolIndicator"),
    );
  }

}

